/* eslint-disable max-statements */
import { useForm, zodResolver } from '@mantine/form';
import { z } from 'zod';

import { Template, TemplateType, TransactionType, TransactionTypes } from '@e-origin/shared';

const editTemplateSchema = z.object({
  name: z.string().min(1, { message: 'Name is required' }),
  deleted: z.boolean().optional(),
  declarants: z.array(z.string()).min(1, { message: 'Declarant is required' }),
  type: z.string().min(1, { message: 'Type is required' }),
  transactionType: z.string().optional().nullable(),
  sheetName: z.string().optional().nullable(),
  templateUrl: z.string().optional().nullable(),
  templateWatermarkUrl: z.string().optional().nullable(),
  templateDynamicPageUrl: z.string().optional().nullable(),
  templateWatermarkDynamicPageUrl: z.string().optional().nullable(),
  groupByShippingMark: z.boolean().optional().nullable(),
  groupLRNByShippingMarks: z.boolean().optional().nullable(),
  groupByOrigin: z.boolean().optional().nullable(),
  groupByTrackingId: z.boolean().optional().nullable(),
  isExport: z.boolean().optional().nullable(),
  mappingFields: z.array(z.string().optional().nullable()).optional().nullable(),
  additionalFixedValues: z.array(z.string().optional().nullable()).optional().nullable(),
  highValueSettings: z
    .object({
      othersMappings: z.array(z.string().optional().nullable()),
      shipmentsMapping: z.object({
        startRowIndex: z.number().optional().nullable(),
        mappingFields: z.array(z.string().optional().nullable()),
      }),
      stakeholdersMapping: z.array(z.string().optional().nullable()),
      packagingList: z.object({
        startRowIndex: z.number().optional().nullable(),
        shippingMarksColumn: z.string().optional().nullable(),
        descriptionOfGoodsColumn: z.string().optional().nullable(),
        quantityCountColumn: z.string().optional().nullable(),
        packagesCountColumn: z.string().optional().nullable(),
        grossWeightColumn: z.string().optional().nullable(),
        netWeightColumn: z.string().optional().nullable(),
      }),
      sendReport: z.object({
        referenceValue: z.string().optional().nullable(),
        webValueChecked: z.string().optional().nullable(),
        suggestedUnitPrice: z.string().optional().nullable(),
        suggestedValue: z.string().optional().nullable(),
        suggestedTaxRate: z.string().optional().nullable(),
        dutyEuro: z.string().optional().nullable(),
        suggestedDutyEuro: z.string().optional().nullable(),
        calculatedImpact: z.string().optional().nullable(),
        suggestedHsCode: z.string().optional().nullable(),
        suggestedDescription: z.string().optional().nullable(),
        LRN: z.string().optional().nullable(),
        goodsItem: z.string().optional().nullable(),
        annotationNote: z.string().optional().nullable(),
        CTinvoiceHSCode: z.string().optional().nullable(),
        CTcodeMatchStatus: z.string().optional().nullable(),
        CTcodeMatchAccuracy100A: z.string().optional().nullable(),
        CTcodeMatchAccuracy100B: z.string().optional().nullable(),
        CTcodeMatchAccuracy100C: z.string().optional().nullable(),
        CTcodeMatchAccuracy90: z.string().optional().nullable(),
        CTinvoiceValue: z.string().optional().nullable(),
        CTwebPrice: z.string().optional().nullable(),
        CTpercentageWebPrice: z.string().optional().nullable(),
        CTstatisticalValue: z.string().optional().nullable(),
        CTreasonableValue: z.string().optional().nullable(),
        CTdutiesInvoiced: z.string().optional().nullable(),
        CTdutiesMatchAccuracy100A: z.string().optional().nullable(),
        CTpreferredHSCode: z.string().optional().nullable(),
        CTpreferredPrice: z.string().optional().nullable(),
      }),
    })
    .optional()
    .nullable(),
  batchMappings: z
    .object({
      otherMappings: z.array(z.string().optional().nullable()),
      additionalFixedValues: z.array(z.string()),
    })
    .optional()
    .nullable(),
});

export const useEditTemplateFormInitializer = ({ template }) => {
  const buildInitialValues = (t: Template) => {
    if (!t?.type) {
      return {};
    }

    let builtTmpl: any = {
      _id: t._id,
      name: t.name || '',
      deleted: t.deleted || false,
      declarants: t.declarants || [],
      type: t.type || null,
      transactionType: typeof t.transactionType === 'string' ? t.transactionType : (t?.transactionType as unknown as TransactionType)?._id || '',
      additionalFixedValues: t.additionalFixedValues || [],
    };

    if (![TemplateType.ARRIVAL_TRANSIT, TemplateType.DEPARTURE_TRANSIT].includes(t.type)) {
      builtTmpl = { ...builtTmpl, mappingFields: t.mappingFields || [] };
    }

    if (t.type === TemplateType.HIGH_VALUE_H1) {
      builtTmpl = {
        ...builtTmpl,
        groupByShippingMark: !!t.groupByShippingMark,
        groupLRNByShippingMarks: !!t.groupLRNByShippingMarks,
        groupByOrigin: !!t.groupByOrigin,
        groupByTrackingId: !!t.groupByTrackingId,
        isExport: t.isExport || false,
        highValueSettings: {
          othersMappings: t.highValueSettings?.othersMappings || [],
          shipmentsMapping: {
            startRowIndex: t.highValueSettings?.shipmentsMapping?.startRowIndex || 0,
            mappingFields: t.highValueSettings?.shipmentsMapping?.mappingFields || [],
          },
          stakeholdersMapping: t.highValueSettings?.stakeholdersMapping || [],
          packagingList: {
            startRowIndex: t.highValueSettings?.packagingList?.startRowIndex || 0,
            shippingMarksColumn: t.highValueSettings?.packagingList?.shippingMarksColumn || '',
            descriptionOfGoodsColumn: t.highValueSettings?.packagingList?.descriptionOfGoodsColumn || '',
            quantityCountColumn: t.highValueSettings?.packagingList?.quantityCountColumn || '',
            packagesCountColumn: t.highValueSettings?.packagingList?.packagesCountColumn || '',
            grossWeightColumn: t.highValueSettings?.packagingList?.grossWeightColumn || '',
            netWeightColumn: t.highValueSettings?.packagingList?.netWeightColumn || '',
          },
          sendReport: {
            referenceValue: t.highValueSettings?.sendReport?.referenceValue || '',
            webValueChecked: t.highValueSettings?.sendReport?.webValueChecked || '',
            suggestedUnitPrice: t.highValueSettings?.sendReport?.suggestedUnitPrice || '',
            suggestedValue: t.highValueSettings?.sendReport?.suggestedValue || '',
            resonableValueRate: t.highValueSettings?.sendReport?.resonableValueRate || '',
            netMargin: t.highValueSettings?.sendReport?.netMargin || '',
            suggestedTaxRate: t.highValueSettings?.sendReport?.suggestedTaxRate || '',
            dutyEuro: t.highValueSettings?.sendReport?.dutyEuro || '',
            suggestedDutyEuro: t.highValueSettings?.sendReport?.suggestedDutyEuro || '',
            calculatedImpact: t.highValueSettings?.sendReport?.calculatedImpact || '',
            suggestedHsCode: t.highValueSettings?.sendReport?.suggestedHsCode || '',
            suggestedDescription: t.highValueSettings?.sendReport?.suggestedDescription || '',
            LRN: t.highValueSettings?.sendReport?.LRN || '',
            goodsItem: t.highValueSettings?.sendReport?.goodsItem || '',
            annotationNote: t.highValueSettings?.sendReport?.annotationNote || '',
            CTinvoiceHSCode: t.highValueSettings?.sendReport?.CTinvoiceHSCode || '',
            CTcodeMatchStatus: t.highValueSettings?.sendReport?.CTcodeMatchStatus || '',
            CTcodeMatchAccuracy100A: t.highValueSettings?.sendReport?.CTcodeMatchAccuracy100A || '',
            CTcodeMatchAccuracy100B: t.highValueSettings?.sendReport?.CTcodeMatchAccuracy100B || '',
            CTcodeMatchAccuracy100C: t.highValueSettings?.sendReport?.CTcodeMatchAccuracy100C || '',
            CTcodeMatchAccuracy90: t.highValueSettings?.sendReport?.CTcodeMatchAccuracy90 || '',
            CTinvoiceValue: t.highValueSettings?.sendReport?.CTinvoiceValue || '',
            CTwebPrice: t.highValueSettings?.sendReport?.CTwebPrice || '',
            CTpercentageWebPrice: t.highValueSettings?.sendReport?.CTpercentageWebPrice || '',
            CTstatisticalValue: t.highValueSettings?.sendReport?.CTstatisticalValue || '',
            CTreasonableValue: t.highValueSettings?.sendReport?.CTreasonableValue || '',
            CTdutiesInvoiced: t.highValueSettings?.sendReport?.CTdutiesInvoiced || '',
            CTdutiesMatchAccuracy100A: t.highValueSettings?.sendReport?.CTdutiesMatchAccuracy100A || '',
            CTpreferredHSCode: t.highValueSettings?.sendReport?.CTpreferredHSCode || '',
            CTpreferredPrice: t.highValueSettings?.sendReport?.CTpreferredPrice || '',
          },
        },
        batchMappings: { otherMappings: t.batchMappings?.otherMappings || [], additionalFixedValues: t.batchMappings?.additionalFixedValues || [] },
      };
      if ([TransactionTypes.B2B_HV, TransactionTypes.B2B_EXPORT].includes((t?.transactionType as unknown as TransactionType)?.typeId)) {
        builtTmpl = { ...builtTmpl, templateUrl: t.templateUrl || '' };
      }
    }

    if (t.type === TemplateType.LOW_VALUE_H7) {
      builtTmpl = {
        ...builtTmpl,
        sheetName: t.sheetName || '',
        skipSplitBatchForSplitShipments: t.skipSplitBatchForSplitShipments || false,
      };
    }

    if (![TemplateType.HIGH_VALUE_H1, TemplateType.LOW_VALUE_H7, TemplateType.ARRIVAL_TRANSIT, TemplateType.DEPARTURE_TRANSIT].includes(t.type)) {
      builtTmpl = {
        ...builtTmpl,
        templateUrl: t.templateUrl || '',
        templateWatermarkUrl: t.templateWatermarkUrl || '',
        templateWatermarkDynamicPageUrl: t.templateWatermarkDynamicPageUrl || '',
        templateDynamicPageUrl: t.templateDynamicPageUrl || '',
      };
    }

    return builtTmpl;
  };

  const form = useForm({
    initialValues: buildInitialValues(template),
    validate: zodResolver(editTemplateSchema),
    validateInputOnBlur: true,
  });

  return {
    form,
    buildInitialValues,
  };
};
